import React, { useEffect, useState, useRef } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import { FileTextIcon, CheckCircleIcon, XCircleIcon, ClockIcon, PercentIcon, UsersIcon, GlobeIcon, BuildingIcon, CalendarIcon, ChevronDownIcon, PencilIcon } from 'lucide-react';
import { BIKPI } from './BIKPI';
import { BICard } from './BICard';
import { BIInsights } from './BIInsights';
import { mockBUs, userComparisonData, trendData, countryData, dealerData, buInsights } from '../../data/biData';
const COLORS = ['#3b82f6', '#10b981', '#ef4444', '#f59e0b', '#8b5cf6'];
type DateFilterMode = 'year' | 'quarter' | 'month';
const currentYear = new Date().getFullYear();
const years = Array.from({
  length: 6
}, (_, i) => currentYear - i);
const quarters = ['Q1', 'Q2', 'Q3', 'Q4'];
const monthsList = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
interface BusinessUnitViewProps {
  buName?: string;
  onBuChange?: (bu: string) => void;
}
export function BusinessUnitView({
  buName = 'North America',
  onBuChange
}: BusinessUnitViewProps) {
  const [dateMode, setDateMode] = useState<DateFilterMode>('year');
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedQuarter, setSelectedQuarter] = useState('Q1');
  const [selectedMonth, setSelectedMonth] = useState('January');
  const [isEditingDate, setIsEditingDate] = useState(false);
  const [isBuOpen, setIsBuOpen] = useState(false);
  const datePickerRef = useRef<HTMLDivElement>(null);
  const buPickerRef = useRef<HTMLDivElement>(null);
  const dateLabel = dateMode === 'year' ? `${selectedYear}` : dateMode === 'quarter' ? `${selectedQuarter} ${selectedYear}` : `${selectedMonth} ${selectedYear}`;
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target as Node)) {
        setIsEditingDate(false);
      }
      if (buPickerRef.current && !buPickerRef.current.contains(event.target as Node)) {
        setIsBuOpen(false);
      }
    }
    if (isEditingDate || isBuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isEditingDate, isBuOpen]);
  return <div className="space-y-6" data-id="element-681">
      {/* BU Identity Header — building icon + region stats */}
      <div className="flex flex-wrap items-center gap-4" data-id="element-682">
        {/* BU Identity */}
        <div className="flex items-center gap-4 mr-auto" data-id="element-683">
          <div className="w-12 h-12 flex items-center justify-center flex-shrink-0" data-id="element-684">
            <img src="https://cdn.magicpatterns.com/uploads/1HYHcTUzSRJofQ3DFDsYcj/58061.png" alt="Business Unit" className="w-12 h-12 filter invert opacity-90" data-id="element-685" />
          </div>
          <div data-id="element-686">
            {/* Region Select */}
            <div className="relative" ref={buPickerRef} data-id="element-687">
              <button onClick={() => setIsBuOpen(!isBuOpen)} className="flex items-center gap-1.5 cursor-pointer focus:outline-none group" data-id="element-688">
                <span className="text-white font-semibold text-[22px] group-hover:text-blue-200 transition-colors" data-id="element-689">
                  {buName}
                </span>
                <ChevronDownIcon size={18} className={`text-blue-400 transition-transform ${isBuOpen ? 'rotate-180' : ''}`} data-id="element-690" />
              </button>
              {isBuOpen && <div className="absolute top-full left-0 mt-1.5 bg-[#0f1d3a] rounded-xl border border-white/10 shadow-2xl py-1 z-50 min-w-[200px]" data-id="element-691">
                  {mockBUs.map(bu => <button key={bu} onClick={() => {
                onBuChange?.(bu);
                setIsBuOpen(false);
              }} className={`w-full text-left px-4 py-2 text-sm transition-colors ${bu === buName ? 'bg-blue-600/20 text-white font-medium' : 'text-blue-200 hover:bg-white/10 hover:text-white'}`} data-id="element-692">
                      {bu}
                    </button>)}
                </div>}
            </div>
            <p className="text-blue-300/70 text-sm leading-tight mt-0.5" data-id="element-693">
              Business Unit quotes analysis and performance
            </p>
            {/* Inline date filter */}
            <div className="relative mt-1" ref={datePickerRef} data-id="element-694">
              <button onClick={() => setIsEditingDate(!isEditingDate)} className="flex items-center gap-2 text-sm text-blue-300 hover:text-white transition-colors group" data-id="element-695">
                <CalendarIcon size={15} className="text-blue-400/70 group-hover:text-blue-300" data-id="element-696" />
                <span className="font-medium" data-id="element-697">{dateLabel}</span>
                <PencilIcon size={12} className="opacity-0 group-hover:opacity-100 transition-opacity text-blue-400" data-id="element-698" />
              </button>

              {/* Date picker popover */}
              {isEditingDate && <div className="absolute top-full left-0 mt-2 bg-[#0f1d3a] rounded-xl border border-white/10 shadow-2xl p-3 z-50 min-w-[240px]" data-id="element-699">
                  <div className="space-y-2.5" data-id="element-700">
                    {/* Period type */}
                    <div data-id="element-701">
                      <p className="text-[10px] uppercase tracking-wider text-blue-400/60 mb-1" data-id="element-702">
                        Period
                      </p>
                      <div className="flex gap-1" data-id="element-703">
                        {(['year', 'quarter', 'month'] as DateFilterMode[]).map(mode => <button key={mode} onClick={() => setDateMode(mode)} className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${dateMode === mode ? 'bg-blue-600 text-white' : 'bg-white/5 text-blue-300 hover:bg-white/10 hover:text-white'}`} data-id="element-704">
                              {mode.charAt(0).toUpperCase() + mode.slice(1)}
                            </button>)}
                      </div>
                    </div>

                    {/* Month selector */}
                    {dateMode === 'month' && <div data-id="element-705">
                        <p className="text-[10px] uppercase tracking-wider text-blue-400/60 mb-1" data-id="element-706">
                          Month
                        </p>
                        <div className="relative" data-id="element-707">
                          <select value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/10 rounded-lg px-3 py-1.5 pr-7 text-xs font-medium text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all cursor-pointer" data-id="element-708">
                            {monthsList.map(m => <option key={m} value={m} className="bg-slate-800 text-white" data-id="element-709">
                                {m}
                              </option>)}
                          </select>
                          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-1.5 text-blue-300" data-id="element-710">
                            <ChevronDownIcon size={12} data-id="element-711" />
                          </div>
                        </div>
                      </div>}

                    {/* Quarter selector */}
                    {dateMode === 'quarter' && <div data-id="element-712">
                        <p className="text-[10px] uppercase tracking-wider text-blue-400/60 mb-1" data-id="element-713">
                          Quarter
                        </p>
                        <div className="flex gap-1" data-id="element-714">
                          {quarters.map(q => <button key={q} onClick={() => setSelectedQuarter(q)} className={`flex-1 px-2 py-1 rounded-md text-xs font-medium transition-colors ${selectedQuarter === q ? 'bg-blue-600 text-white' : 'bg-white/5 text-blue-300 hover:bg-white/10 hover:text-white'}`} data-id="element-715">
                              {q}
                            </button>)}
                        </div>
                      </div>}

                    {/* Year selector */}
                    <div data-id="element-716">
                      <p className="text-[10px] uppercase tracking-wider text-blue-400/60 mb-1" data-id="element-717">
                        Year
                      </p>
                      <div className="relative" data-id="element-718">
                        <select value={selectedYear} onChange={e => setSelectedYear(Number(e.target.value))} className="w-full appearance-none bg-white/10 border border-white/10 rounded-lg px-3 py-1.5 pr-7 text-xs font-medium text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all cursor-pointer" data-id="element-719">
                          {years.map(y => <option key={y} value={y} className="bg-slate-800 text-white" data-id="element-720">
                              {y}
                            </option>)}
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-1.5 text-blue-300" data-id="element-721">
                          <ChevronDownIcon size={12} data-id="element-722" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>}
            </div>
          </div>
        </div>

        {/* Active Countries Panel */}
        <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl border border-white/10 px-5 py-3" data-id="element-723">
          <div className="flex items-center gap-3" data-id="element-724">
            <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center" data-id="element-725">
              <GlobeIcon size={18} className="text-blue-200/70" data-id="element-726" />
            </div>
            <div data-id="element-727">
              <p className="text-xs text-blue-300/70 leading-tight" data-id="element-728">
                Active Countries
              </p>
              <p className="text-xl font-bold text-white leading-tight" data-id="element-729">8</p>
            </div>
          </div>
        </div>

        {/* Active Dealers Panel */}
        <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl border border-white/10 px-5 py-3" data-id="element-730">
          <div className="flex items-center gap-3" data-id="element-731">
            <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center" data-id="element-732">
              <BuildingIcon size={18} className="text-blue-200/70" data-id="element-733" />
            </div>
            <div data-id="element-734">
              <p className="text-xs text-blue-300/70 leading-tight" data-id="element-735">
                Active Dealers
              </p>
              <p className="text-xl font-bold text-white leading-tight" data-id="element-736">42</p>
            </div>
          </div>
        </div>

        {/* Active Users Panel */}
        <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl border border-white/10 px-5 py-3" data-id="element-737">
          <div className="flex items-center gap-3" data-id="element-738">
            <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center" data-id="element-739">
              <UsersIcon size={18} className="text-blue-200/70" data-id="element-740" />
            </div>
            <div data-id="element-741">
              <p className="text-xs text-blue-300/70 leading-tight" data-id="element-742">
                Active Users
              </p>
              <p className="text-xl font-bold text-white leading-tight" data-id="element-743">12</p>
            </div>
          </div>
        </div>
      </div>

      {/* KPIs Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-744">
        <BIKPI title="BU Total Quotes" value={450} trend={8.5} icon={<FileTextIcon size={18} data-id="element-746" />} color="blue" delay={0.1} data-id="element-745" />
        <BIKPI title="BU Won Quotes" value={280} trend={5.2} icon={<CheckCircleIcon size={18} data-id="element-748" />} color="emerald" delay={0.2} data-id="element-747" />
        <BIKPI title="BU Win Rate" value="77.7%" trend={1.5} icon={<PercentIcon size={18} data-id="element-750" />} color="teal" delay={0.3} data-id="element-749" />
        <BIKPI title="Active Quotes" value={70} icon={<ClockIcon size={18} data-id="element-752" />} color="amber" delay={0.4} data-id="element-751" />
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-753">
        <BICard title="BU Quotes Trend" className="lg:col-span-2" data-id="element-754">
          <div className="h-80" data-id="element-755">
            <ResponsiveContainer width="100%" height="100%" data-id="element-756">
              <LineChart data={trendData.map(d => ({
              ...d,
              total: Math.floor(d.total * 0.4),
              won: Math.floor(d.won * 0.4),
              lost: Math.floor(d.lost * 0.4)
            }))} margin={{
              top: 20,
              right: 30,
              left: 0,
              bottom: 5
            }} data-id="element-757">
                <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" vertical={false} data-id="element-758" />
                <XAxis dataKey="month" stroke="#93c5fd" fontSize={12} tickLine={false} axisLine={false} data-id="element-759" />
                <YAxis stroke="#93c5fd" fontSize={12} tickLine={false} axisLine={false} data-id="element-760" />
                <Tooltip contentStyle={{
                backgroundColor: '#0f1d3a',
                borderColor: '#ffffff20',
                borderRadius: '8px',
                color: '#fff'
              }} data-id="element-761" />
                <Legend wrapperStyle={{
                fontSize: '12px',
                color: '#93c5fd'
              }} data-id="element-762" />
                <Line type="monotone" dataKey="total" name="Total Quotes" stroke="#3b82f6" strokeWidth={3} dot={{
                r: 4,
                fill: '#3b82f6',
                strokeWidth: 2,
                stroke: '#0f1d3a'
              }} activeDot={{
                r: 6
              }} data-id="element-763" />
                <Line type="monotone" dataKey="won" name="Won Quotes" stroke="#10b981" strokeWidth={3} dot={{
                r: 4,
                fill: '#10b981',
                strokeWidth: 2,
                stroke: '#0f1d3a'
              }} data-id="element-764" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </BICard>

        <BICard title="Quotes by User" data-id="element-765">
          <div className="h-72" data-id="element-766">
            <ResponsiveContainer width="100%" height="100%" data-id="element-767">
              <BarChart data={userComparisonData} margin={{
              top: 20,
              right: 30,
              left: 0,
              bottom: 5
            }} data-id="element-768">
                <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" vertical={false} data-id="element-769" />
                <XAxis dataKey="name" stroke="#93c5fd" fontSize={12} tickLine={false} axisLine={false} data-id="element-770" />
                <YAxis stroke="#93c5fd" fontSize={12} tickLine={false} axisLine={false} data-id="element-771" />
                <Tooltip contentStyle={{
                backgroundColor: '#0f1d3a',
                borderColor: '#ffffff20',
                borderRadius: '8px',
                color: '#fff'
              }} itemStyle={{
                color: '#fff'
              }} data-id="element-772" />
                <Legend wrapperStyle={{
                fontSize: '12px',
                color: '#93c5fd'
              }} data-id="element-773" />
                <Bar dataKey="won" name="Won" stackId="a" fill="#10b981" radius={[0, 0, 4, 4]} data-id="element-774" />
                <Bar dataKey="active" name="Active" stackId="a" fill="#3b82f6" data-id="element-775" />
                <Bar dataKey="lost" name="Lost" stackId="a" fill="#ef4444" radius={[4, 4, 0, 0]} data-id="element-776" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </BICard>

        <BICard title="Win Rate by User" data-id="element-777">
          <div className="h-72" data-id="element-778">
            <ResponsiveContainer width="100%" height="100%" data-id="element-779">
              <BarChart data={userComparisonData} layout="vertical" margin={{
              top: 20,
              right: 30,
              left: 60,
              bottom: 5
            }} data-id="element-780">
                <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" horizontal={false} data-id="element-781" />
                <XAxis type="number" domain={[0, 100]} stroke="#93c5fd" fontSize={12} tickLine={false} axisLine={false} data-id="element-782" />
                <YAxis dataKey="name" type="category" stroke="#93c5fd" fontSize={12} tickLine={false} axisLine={false} data-id="element-783" />
                <Tooltip contentStyle={{
                backgroundColor: '#0f1d3a',
                borderColor: '#ffffff20',
                borderRadius: '8px',
                color: '#fff'
              }} formatter={value => [`${value}%`, 'Win Rate']} data-id="element-784" />
                <Bar dataKey="winRate" name="Win Rate" fill="#8b5cf6" radius={[0, 4, 4, 0]} barSize={20} data-id="element-785">
                  {userComparisonData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.winRate > 80 ? '#10b981' : entry.winRate < 75 ? '#ef4444' : '#8b5cf6'} data-id="element-786" />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </BICard>
      </div>

      {/* Tables & Insights Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-787">
        <div className="lg:col-span-2 space-y-6" data-id="element-788">
          <BICard title="User Performance" data-id="element-789">
            <div className="overflow-x-auto" data-id="element-790">
              <table className="w-full text-sm text-left" data-id="element-791">
                <thead className="text-xs text-blue-300 uppercase bg-white/5 border-b border-white/10" data-id="element-792">
                  <tr data-id="element-793">
                    <th className="px-4 py-3 font-medium" data-id="element-794">User</th>
                    <th className="px-4 py-3 font-medium text-right" data-id="element-795">Total</th>
                    <th className="px-4 py-3 font-medium text-right" data-id="element-796">Won</th>
                    <th className="px-4 py-3 font-medium text-right" data-id="element-797">Active</th>
                    <th className="px-4 py-3 font-medium text-right" data-id="element-798">
                      Win Rate
                    </th>
                  </tr>
                </thead>
                <tbody data-id="element-799">
                  {userComparisonData.map((user, idx) => <tr key={idx} className="border-b border-white/5 hover:bg-white/5 transition-colors" data-id="element-800">
                      <td className="px-4 py-3 font-medium text-white flex items-center" data-id="element-801">
                        <div className="w-6 h-6 rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center text-xs mr-2 font-bold" data-id="element-802">
                          {user.name.split(' ').map(n => n[0]).join('')}
                        </div>
                        {user.name}
                      </td>
                      <td className="px-4 py-3 text-right text-blue-100" data-id="element-803">
                        {user.total}
                      </td>
                      <td className="px-4 py-3 text-right text-emerald-400" data-id="element-804">
                        {user.won}
                      </td>
                      <td className="px-4 py-3 text-right text-blue-400" data-id="element-805">
                        {user.active}
                      </td>
                      <td className="px-4 py-3 text-right text-white" data-id="element-806">
                        <span className={`px-2 py-1 rounded-full text-xs ${user.winRate > 80 ? 'bg-emerald-500/20 text-emerald-400' : 'bg-blue-500/20 text-blue-400'}`} data-id="element-807">
                          {user.winRate}%
                        </span>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </BICard>

          <BICard title="Top Dealers in BU" data-id="element-808">
            <div className="overflow-x-auto" data-id="element-809">
              <table className="w-full text-sm text-left" data-id="element-810">
                <thead className="text-xs text-blue-300 uppercase bg-white/5 border-b border-white/10" data-id="element-811">
                  <tr data-id="element-812">
                    <th className="px-4 py-3 font-medium" data-id="element-813">Dealer</th>
                    <th className="px-4 py-3 font-medium" data-id="element-814">Country</th>
                    <th className="px-4 py-3 font-medium text-right" data-id="element-815">Total</th>
                    <th className="px-4 py-3 font-medium text-right" data-id="element-816">Won</th>
                    <th className="px-4 py-3 font-medium text-right" data-id="element-817">
                      Win Rate
                    </th>
                  </tr>
                </thead>
                <tbody data-id="element-818">
                  {dealerData.filter(d => d.bu === 'North America' || d.bu === 'LATAM').map((dealer, idx) => <tr key={idx} className="border-b border-white/5 hover:bg-white/5 transition-colors" data-id="element-819">
                        <td className="px-4 py-3 font-medium text-white" data-id="element-820">
                          {dealer.name}
                        </td>
                        <td className="px-4 py-3 text-blue-300" data-id="element-821">
                          {dealer.country}
                        </td>
                        <td className="px-4 py-3 text-right text-blue-100" data-id="element-822">
                          {dealer.total}
                        </td>
                        <td className="px-4 py-3 text-right text-emerald-400" data-id="element-823">
                          {dealer.won}
                        </td>
                        <td className="px-4 py-3 text-right text-white" data-id="element-824">
                          {dealer.winRate}%
                        </td>
                      </tr>)}
                </tbody>
              </table>
            </div>
          </BICard>
        </div>

        <div className="lg:col-span-1" data-id="element-825">
          <BIInsights insights={buInsights} data-id="element-826" />
        </div>
      </div>
    </div>;
}